import {Core} from './core.js'

export default class BannerTop {

	/** @type {HTMLElement} */ #el;
	/** @type {ResizeObserver} */ #resizeObserver;

	constructor() {
    if (Core.isAdmin()) {
      return
    }

    this.DISMISSED_KEY = "banner-top-dismissed";

    document.addEventListener( 'DOMContentLoaded', this.init.bind(this) );
	}

  init() {
    const el = document.querySelector("[data-banner-top]");
    if ( !el ) {
      return;
    }

		this.#el = el;
    const lang = document.documentElement.getAttribute('lang');
    const textContent = el.querySelector('.banner-top__text')?.textContent;

    // Make DISMISSED_KEY dynamic based on lang + content (prevent masking a new banner).
    // The text is reduced to a hash so the key stays free of spaces & special characters.
    this.DISMISSED_KEY = this.DISMISSED_KEY + '-' + lang + '-' + this.#hash(textContent);

		const fingerprint = this.#fingerprint(el);
		const stored = window.sessionStorage.getItem(this.DISMISSED_KEY);

		if (stored === fingerprint) {
			this.#dismiss(false);
			return;
		}

		// Different or missing fingerprint → new banner, clear stale entry
		window.sessionStorage.removeItem(this.DISMISSED_KEY);

		this.#el
			.querySelector("[data-banner-close]")
			?.addEventListener("click", () => this.#dismiss(true));

		this.#resizeObserver = new window.ResizeObserver(() => this.#syncHeight());
		this.#resizeObserver.observe(this.#el);
		this.#syncHeight();
  }

	/** Lightweight djb2 fingerprint of the banner's text + link content. */
	#fingerprint(el) {
		const inner = el.querySelector('.banner-top__inner');
		return this.#hash(inner ? inner.textContent : el.textContent);
	}

	/**
	 * djb2 hash of a string, returned as an unsigned decimal.
	 * Digits only: safe to embed in a storage key whatever the source text contains.
	 *
	 * @param {string|null|undefined} value
	 * @returns {string}
	 */
	#hash(value) {
		const text = String(value ?? "").trim().replace(/\s+/g, " ");
		let hash = 5381;
		for (let i = 0; i < text.length; i++) {
			hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0;
		}
		return String(hash >>> 0);
	}

	/** Sync --banner-top-height and --header-visible-height CSS variables to actual rendered height. */
	#syncHeight() {
		const bannerH = this.#el.offsetHeight;
		const root = document.documentElement;

		root.style.setProperty("--banner-top-height", `${bannerH}px`);

		const headerH =
			parseFloat(window.getComputedStyle(root).getPropertyValue("--header-height")) || 0;
		root.style.setProperty("--header-visible-height", `${headerH + bannerH}px`);
	}

	/**
	 * @param {boolean} persist - Whether to store the dismissal in sessionStorage.
	 */
	#dismiss(persist) {
		if (persist) {
			window.sessionStorage.setItem(
				this.DISMISSED_KEY,
				this.#fingerprint(this.#el),
			);
		}

		this.#resizeObserver?.disconnect();
		this.#el.remove();
		document.body.classList.remove("show-banner-top");
		document.documentElement.style.removeProperty("--banner-top-height");

		// Restore --header-visible-height to plain header height (no banner offset).
		// If the Header hasn't initialised yet (dismiss on load), just remove the property
		// and let the Header set it correctly when it boots.
		const headerH = window
			.getComputedStyle(document.documentElement)
			.getPropertyValue("--header-height")
			.trim();
		if (headerH) {
			document.documentElement.style.setProperty("--header-visible-height", headerH);
		} else {
			document.documentElement.style.removeProperty("--header-visible-height");
		}
	}
}
